import { combineEpics, ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  ignoreElements,
  map,
  mergeMap,
  takeUntil,
} from 'rxjs/operators';

import {
  PUBLIC_SOCKET_CONNECTED,
  SUBSCRIBE_LAST_TRADED,
  UNSUBSCRIBE_ALL,
  UNSUBSCRIBE_LAST_TRADED,
} from 'actionTypes/socket';
import { TOP_4_SYMBOLS } from 'constants/constants';
import { isNotEmpty } from 'ramdax';
import { useLastTradedStoreBase } from 'stores/lastTraded/lastTraded.store';

import { publicWsSubject } from './publicSocket';

const updateLastTradedPrices = data => {
  const lastTradedStore = useLastTradedStoreBase.getState();
  const { actions } = lastTradedStore || {};
  // data contains object like {"BTCUSD": 90007}
  actions.updateLastTradedPrices(data?.data);
};

const payloadGenerator = (type, name, symbols) => {
  return {
    type,
    payload: {
      channels: [
        {
          name,
          symbols,
        },
      ],
    },
  };
};

const lastTradedChannel = symbols =>
  publicWsSubject.multiplex(
    () => payloadGenerator('subscribe', 'web_ltp', symbols),
    () => payloadGenerator('unsubscribe', 'web_ltp', symbols),
    message => message.type === 'web_ltp'
  );

export const subscribeLastTradedChannelEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_LAST_TRADED),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap((action: { payload: string[] }) => {
      const symbols = action.payload || [];

      if (symbols.length === 0) {
        return EMPTY;
      }

      // Track subscribed symbols
      const lastTradedStore = useLastTradedStoreBase.getState();
      lastTradedStore.actions.setSubscribedSymbols(symbols);

      return lastTradedChannel(symbols).pipe(
        takeUntil(action$.ofType(UNSUBSCRIBE_ALL, UNSUBSCRIBE_LAST_TRADED)),
        finalize(() => {
          // Clear subscription state on unsubscribe
          const lastTradedStoreData = useLastTradedStoreBase.getState();
          lastTradedStoreData.actions.setSubscribedSymbols([]);
        }),
        catchError(err => {
          console.error('Last traded channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateLastTradedPrices),
    ignoreElements()
  );

/**
 * Epic to resubscribe to web_ltp when public socket reconnects
 * This handles the case where the internet disconnects and reconnects
 */
export const resubscribeLastTradedOnSocketReconnectEpic = (action$, state$) =>
  action$.pipe(
    ofType(PUBLIC_SOCKET_CONNECTED),
    filter(() => {
      // Only resubscribe if not already subscribed
      const lastTradedStore = useLastTradedStoreBase.getState();
      const subscribedSymbols = lastTradedStore.state?.subscribedSymbols || [];
      return subscribedSymbols.length === 0;
    }),
    filter(() => {
      // Only resubscribe if user is on the home page
      const pathname = state$?.value?.router?.location?.pathname || '';
      const isHomePage = pathname.includes('/home');
      return isHomePage;
    }),
    map(() => {
      // Resubscribe to the same channels that were previously subscribed
      return { type: SUBSCRIBE_LAST_TRADED, payload: [TOP_4_SYMBOLS] };
    })
  );

export default combineEpics(
  subscribeLastTradedChannelEpic,
  resubscribeLastTradedOnSocketReconnectEpic
);
